import React, {ReactNode} from "react";

import cx from "classnames";
import ReactModal from "react-modal";

import reactNodeToString from "@globals/utils/reactNodeToString";

import NomiconIcon from "@components/NomiconIcon";
import TransparentButton from "@components/TransparentButton";

import styles from "./Modal.scss";

/* eslint-disable no-process-env */
// to avoid errors with tests
if (process.env.NODE_ENV !== "test") {
  // we need to set this for accesibility
  ReactModal.setAppElement("#root");
}

export type ModalProps = {
  isOpen: boolean;
  modalComponent: React.ElementType;
  onClose?: () => void;
  onCloseButtonClick?: () => void;
  onAfterClose?: () => void;
  showCloseButton?: boolean;
  title?: ReactNode;
  modalClassName?: string;
  closeButtonClassName?: string;
  // any other props
  [x: string]: any;
};

export default function Modal({
  isOpen,
  showCloseButton = true,
  onClose,
  onCloseButtonClick,
  onAfterClose,
  title,
  modalClassName,
  closeButtonClassName,
  modalComponent: ModalComponent,
  ...otherProps
}: ModalProps): JSX.Element {
  const handleCloseButtonClick = () => {
    onCloseButtonClick?.();
    onClose?.();
  };
  // the conditional rendering is addded to avoid excessive amount of nodes in DOM
  // https://github.com/reactjs/react-modal/issues/760
  return isOpen ? (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      onAfterClose={onAfterClose}
      contentLabel={reactNodeToString(title)}
      overlayClassName={styles.overlay}
      className={cx(styles.modalWrapper, modalClassName)}
      bodyOpenClassName={styles.disableScroll}
    >
      {showCloseButton && (
        <TransparentButton
          data-testid="modal-close"
          aria-label="Close"
          onClick={handleCloseButtonClick}
          className={cx(styles.closeBtn, closeButtonClassName)}
        >
          <NomiconIcon icon="nomicon-dismiss-filled" />
        </TransparentButton>
      )}
      <ModalComponent title={title} onClose={onClose} {...otherProps} />
    </ReactModal>
  ) : (
    <></>
  );
}
